/**
 * Utility functions for formatting time and duration values.
 *
 * @module utils/time
 * @author Snowfly
 * @version 1.0.0
 */

/**
 * Converts an elapsed time input into a formatted string representation.
 *
 * The input can either be a string in the format of hours, minutes, and seconds (e.g., "2h 30m 45s"),
 * or a numeric value representing seconds. The method calculates the total time
 * in seconds and returns a formatted string with appropriate hours, minutes, and seconds.
 *
 * If the input is a numeric value, it is assumed to be in seconds and will be formatted accordingly.
 *
 * If the input is a string, it will parse the values for hours (`h`), minutes (`m`), and seconds (`s`)
 * and compute the total time in seconds before converting it back to a formatted string.
 *
 * Returns "0m 0s" if the input does not contain any valid time components.
 *
 * @param {string|number} elapsedTime - The elapsed time to be formatted. Can be a string
 *                                       combining hours, minutes, and seconds, or a numeric value in seconds.
 * @returns {string} A formatted string representing the elapsed time in the format "Xh Ym Zs".
 */
export const formatElapsedTime = (elapsedTime) => {
    const secondsPerHour = 3600,
        secondsPerMinute = 60;
    let seconds = 0;

    if (typeof(elapsedTime) === 'string') {
        const timeParts = elapsedTime.match(/(\d+\D+)/g);
        timeParts.forEach((timePart) => {
            if (timePart.indexOf('h') > -1) {
                seconds += parseFloat(timePart) * secondsPerHour;
            }
            if (timePart.indexOf('m') > -1) {
                seconds += parseFloat(timePart) * secondsPerMinute;
            }
            if (timePart.indexOf('s') > -1) {
                seconds += (parseFloat(timePart) * 100) / 100;
            }
        });
    } else {
        seconds = parseFloat(elapsedTime);
    }

    let parts = [],
        hours = Math.floor(seconds / secondsPerHour),
        minutes = 0,
        fmt = new Intl.NumberFormat(
            undefined,
            {
                maximumFractionDigits: 0
            }
        );

    if (hours) {
        parts.push(hours + 'h');
        seconds -= hours * secondsPerHour;
    }

    minutes = Math.floor(seconds / secondsPerMinute);
    if (minutes) {
        parts.push(minutes + 'm');
        seconds -= minutes * secondsPerMinute;
    }
    if (seconds) {
        parts.push(fmt.format(seconds) + 's');

    }

    return parts.length ? parts.join(' ') : '0m 0s';
};

/**
 * Combines a current session's elapsed time with a previously saved elapsed time from a resumed
 * level and returns the total duration in seconds.
 *
 * Both values may be plain numbers (seconds, as produced by `Timer.jsx`) or legacy strings in
 * `"Xm Ys"` format. When both are numeric they are summed directly. When either is a string the
 * function parses the old `MM.SS`-float representation before converting to seconds.
 *
 * @param {number|string} currentElapsed - Elapsed time for the current session.
 * @param {number|string} previousElapsed - Previously saved elapsed time from a resumed level.
 * @returns {number} Total elapsed time in seconds.
 */
export const combinedElapsedSeconds = (currentElapsed, previousElapsed) => {
    if (!isNaN(currentElapsed) && !isNaN(previousElapsed)) {
        return Number(currentElapsed) + Number(previousElapsed);
    }

    const toFloat = (val) =>
        parseFloat(('' + val).replace(/\s/g, '').replace('m', '.'));
    const floatVal = toFloat(currentElapsed) + toFloat(previousElapsed);

    return parseInt(floatVal) * 60 + parseInt((floatVal % 1).toFixed(2).substring(2));
};
